import React from 'react';
import { Box, Typography, Grid, Link } from '@mui/material';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../LinkWithIcon/LinkWithIconComponent';
import Image from '../../Image/Image';

const VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/FourColumnCardsVector.svg';

const SUBTITLE_TO_CARDS_GAP = 40;
const CARD_GAP = 3;

const FourColumnCardsComponent = ({ data }) => {
  const { title, description, footer, cards, footerLink } = data;

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '1400px',
          borderRadius: '48px',
          paddingTop: '32px',
          px: { xs: 3, sm: 4, md: 6 },
          boxSizing: 'border-box',
          textAlign: 'center',
          overflow: 'visible',
        }}
      >
        {/* Title */}
        <ScrollFadeWrapper variant="text">
          <Typography
            component={data.component || 'h2'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mb: '16px',
            }}
          >
            {title}
          </Typography>
        </ScrollFadeWrapper>

        {/* Description */}
        <ScrollFadeWrapper variant="text" delay={0.2}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              maxWidth: '1010px',
              fontWeight: 400,
              fontSize: '1.5rem',
              lineHeight: '1.75rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mb: `${SUBTITLE_TO_CARDS_GAP}px`,
              textAlign: 'center',
              mx: 'auto',
            }}
          >
            {description}
          </Typography>
        </ScrollFadeWrapper>

        {/* Cards Grid */}
        <Grid
          container
          spacing={CARD_GAP}
          sx={{
            mb: '40px',
            justifyContent: 'center',
          }}
        >
          {cards.map((card, index) => (
            <Grid
              item
              key={index}
              xs={12}
              sm={6}
              md={4}
              lg={3}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <ScrollFadeWrapper delay={0.4 + index * 0.08}>
                <Box
                  sx={{
                    minHeight: '410px',
                    width: '100%',
                    maxWidth: '286px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '48px',
                    padding: '32px',
                    boxShadow:
                      '0px 1px 2px 0px rgba(162, 172, 177, 0.2), 0px 12px 24px -4px rgba(22, 47, 59, 0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    textAlign: 'center',
                  }}
                >
                  {/* Vector with Icon */}
                  <Box
                    sx={{
                      position: 'relative',
                      mb: '32px',
                      width: '102px',
                      height: '102px',
                    }}
                  >
                    {/* Cloudflare Vector */}
                    <Image
                      width={102}
                      height={102}
                      src={VECTOR_URL}
                      alt="Comparison cards graphic"
                      sx={{ width: '102px', height: '102px' }}
                    />

                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        width={42}
                        height={42}
                        src={card.icon}
                        alt={card.imgAltTag || card.title}
                        title={card.imageAltTitle || card.title}
                        style={{ width: '42px', height: '42px' }}
                      />
                    </Box>
                  </Box>

                  {/* Title */}
                  <Typography
                    component={card.component || 'h3'}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      lineHeight: '1.75rem',
                      letterSpacing: '-0.003em',
                      color: '#162F3B',
                      mb: '16px',
                    }}
                  >
                    {card.title}
                  </Typography>

                  {/* Body */}
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      color: '#162F3B',
                      textAlign: 'left',
                      letterSpacing: '-0.002em',
                    }}
                  >
                    {card.body}
                  </Typography>

                  {card.linkText && (
                    <Link
                      href={card.link}
                      target="_blank"
                      underline="none"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        color: '#A2ACB1',
                        mt: '24px',
                        '&:hover': {
                          color: '#9EA900',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {card.linkText}
                    </Link>
                  )}
                </Box>
              </ScrollFadeWrapper>
            </Grid>
          ))}
        </Grid>

        {/* Footer */}
        <LinkWithIconComponent
          href={footerLink || undefined}
          iconSize="1rem"
          sx={{
            textAlign: 'center',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#162F3B',
            maxWidth: '596px',
          }}
        >
          {footer}
        </LinkWithIconComponent>
      </Box>
    </ScrollFadeSection>
  );
};

export default FourColumnCardsComponent;
